import { useState, useEffect, useCallback } from 'react';
import { limitsApi, type PlanLimits } from '@features/subscription/api/limitsApi';

interface UsePlanLimitsReturn {
  planLimits: PlanLimits | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and cache plan limits
 * Caches results to avoid repeated API calls during component lifecycle
 */
export function usePlanLimits(): UsePlanLimitsReturn {
  const [planLimits, setPlanLimits] = useState<PlanLimits | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchLimits = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const limits = await limitsApi.getPlanLimits();
      setPlanLimits(limits);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch plan limits'));
      // Default to restrictive limits on error (fail-safe)
      setPlanLimits({
        minScheduleInterval: 60,
        maxAgents: 1,
        maxStorageGB: 10,
        retentionDays: 7,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  return {
    planLimits,
    isLoading,
    error,
    refetch: fetchLimits,
  };
}
